/**
 * ErrorBoundary - React Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the closing disclosure component tree,
 * logs those errors, and displays a friendly error message instead of crashing.
 *
 * Usage:
 * <ErrorBoundary onError={handleError}>
 *   <ClosingDisclosureView {...props} />
 * </ErrorBoundary>
 */

import React, { ReactNode, ReactElement } from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { logger } from 'utils/logger';

const useStyles = makeStyles((theme: any) => ({
  errorContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderLeft: `4px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  errorTitle: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  errorMessage: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  errorDetails: {
    backgroundColor: '#fff3e0',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    maxHeight: '150px',
    overflow: 'auto',
  },
  retryButton: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  contactText: {
    marginTop: theme.spacing(2),
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
}));

interface ErrorBoundaryProps {
	children: ReactNode;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
	fallback?: (error: Error, retry: () => void) => ReactElement;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: React.ErrorInfo | null;
	errorCount: number;
}

interface ErrorBoundaryFallbackProps {
	error: Error;
	errorInfo: React.ErrorInfo | null;
	onRetry: () => void;
}

/**
 * Default Error Boundary Fallback UI Component
 */
const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  error,
  errorInfo,
  onRetry,
}) => {
  const { t } = useTranslation('closingDisclosure');
  const classes = useStyles();

  return (
    <Box padding={3}>
      <Paper className={classes.errorContainer}>
        <Typography variant="h6" className={classes.errorTitle}>
          {t('ui-error-generic')}
        </Typography>
        <Typography className={classes.errorMessage}>
          {t('ui-retry-message')}
        </Typography>

        {/* Show error details in development */}
        {process.env.NODE_ENV === 'development' && errorInfo && (
          <div className={classes.errorDetails}>
            <strong>Error:</strong>
            <pre>{error.toString()}</pre>
            <strong>Stack:</strong>
            <pre>{errorInfo.componentStack}</pre>
          </div>
        )}

        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            className={classes.retryButton}
          >
            {t('ui-retry')}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { window.location.href = '/'; }}
            className={classes.retryButton}
          >
            {t('ui-back')}
          </Button>
        </Box>

        <Typography className={classes.contactText}>
          {t('ui-support-contact')}
        </Typography>
      </Paper>
    </Box>
  );
};

/**
 * ErrorBoundary component that catches and handles errors gracefully
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    if (process.env.NODE_ENV !== 'production') {
      logger.error('ClosingDisclosureView Error:', error);
      logger.error('Error Info:', errorInfo);
    }

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call parent error handler if provided
    const { onError } = this.props;
    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.handleRetry);
      }

      // Default error UI
      return <ErrorBoundaryFallback error={error} errorInfo={errorInfo} onRetry={this.handleRetry} />;
    }

    return children;
  }
}

export default ErrorBoundary;
